import React, { useState } from "react";

// reactstrap components
import {
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Carousel,
  CarouselItem,
  CarouselControl,
  CarouselIndicators,
  Container,
  Row,
  Col,
} from "reactstrap";
// core components
import { useNavigate } from "react-router-dom";
import AuthHeader from "components/Headers/AuthHeader.js";
import Cookies from "universal-cookie";
import { API } from "services/API";
import {
  AppCta,
  PaymentSafetyLine,
  UsageProofStrip,
  Testimonials,
  FaqList,
  useFaqSchema,
  detectDeviceType,
} from "components/Public/PublicBlocks";
import {
  PLATFORM_FEE_PERCENT,
  PRO_PAYOUT_PERCENT,
  OBJECTION_FAQS,
  FACEBOOK_URL,
} from "variables/publicContent";

const items = [
  {
    src: require("assets/img/carousel_ss/Apple iPhone 16 Pro Max Screenshot 1.png"),
    altText:
      "Bite Logr app start screen: pros upload catch photos, create subscription plans and get paid monthly; anglers follow pros",
  },
  {
    src: require("assets/img/carousel_ss/Apple iPhone 16 Pro Max Screenshot 2.png"),
    altText:
      "Bite Logr app Add Paid Plan screen where a pro sets a monthly price and picks which catch details (location, lure, notes, photo) subscribers see",
  },
  {
    src: require("assets/img/carousel_ss/Apple iPhone 16 Pro Max Screenshot 3.png"),
    altText:
      "Bite Logr app home screen tracking last catch, catches this month, best lure and total fish caught",
  },
  {
    src: require("assets/img/carousel_ss/Apple iPhone 16 Pro Max Screenshot 4.png"),
    altText:
      "Bite Logr app catch details from a followed pro: a 39 lb blue catfish with lure, location, weather and wind",
  },
  {
    src: require("assets/img/carousel_ss/Apple iPhone 16 Pro Max Screenshot 5.png"),
    altText:
      "Bite Logr app Fish Map showing catch locations pinned on a local river",
  },
  {
    src: require("assets/img/carousel_ss/Apple iPhone 16 Pro Max Screenshot 6.png"),
    altText:
      "Bite Logr app Trip Planner matching today's weather, wind, pressure and moon phase to past catches to pick a bait",
  },
];

export default function Home() {
  const [deviceType, setDeviceType] = useState("Web");
  const [activeIndex, setActiveIndex] = useState(0);
  const [animating, setAnimating] = useState(false);
  useFaqSchema(OBJECTION_FAQS);

  const next = () => {
    if (animating) return;
    const nextIndex = activeIndex === items.length - 1 ? 0 : activeIndex + 1;
    setActiveIndex(nextIndex);
  };

  const previous = () => {
    if (animating) return;
    const nextIndex = activeIndex === 0 ? items.length - 1 : activeIndex - 1;
    setActiveIndex(nextIndex);
  };

  const goToIndex = (newIndex) => {
    if (animating) return;
    setActiveIndex(newIndex);
  };

  const slides = items.map((item) => {
    return (
      <CarouselItem
        onExiting={() => setAnimating(true)}
        onExited={() => setAnimating(false)}
        key={item.src}
      >
        <img src={item.src} alt={item.altText} width="100%" />
      </CarouselItem>
    );
  });

  let cookies = new Cookies();
  const redir = useNavigate();
  React.useEffect(() => {
    if (
      cookies.get("fishuem") !== undefined &&
      cookies.get("fishuid") !== undefined &&
      cookies.get("fishuimg") !== undefined &&
      cookies.get("fishuname") !== undefined
    ) {
      localStorage.setItem("fishuem", cookies.get("fishuem"));
      localStorage.setItem("fishuid", cookies.get("fishuid"));
      localStorage.setItem("fishuimg", cookies.get("fishuimg"));
      localStorage.setItem("fishuname", cookies.get("fishuname"));
      API.interceptors.request.use(function (config) {
        config.headers = {
          "Content-Type": "multipart/form-data",
          "X-EncId": localStorage.getItem("fishuid"),
          "X-EncEm": localStorage.getItem("fishuem"),
        };
        return config;
      });
      redir("/admin/homecatchlist");
    }
    document.title = "Bite Logr - Pro fishing patterns for your local waters";
    setDeviceType(detectDeviceType());
  }, []);

  return (
    <>
      <AuthHeader deviceType={deviceType} />
      <Container className="mt--9 pb-5">
        <Row className="justify-content-center">
          <Col lg="6">
            <Card className="card-pricing border-0 text-center mb-4">
              <CardHeader className="bg-transparent">
                <h1 className="h2 text-uppercase ls-1 text-bold pt-3 mb-0">
                  Monetize Your Time on the Water
                </h1>
              </CardHeader>
              <CardBody className="px-lg-5">
                <div className="text-center mb-4">
                  <img
                    alt="Bite Logr logo"
                    style={{ maxHeight: "100px" }}
                    src={require("assets/img/brand/biteLogrLogos/LOGO.png")}
                  />
                </div>
                <p className="text-lead">
                  Turn your fishing knowledge into passive income
                </p>
                <p className="text-lead">
                  Bite Logr lets pro anglers get paid for their patterns. Share
                  your fish, set your price and build a loyal following of
                  anglers who want to learn from you.
                </p>
                <p className="text-muted mb-0">
                  Bite Logr is a fishing app where anglers pay a monthly
                  subscription to follow pros on their local waters and see
                  what the pros are catching, on what, and when.
                </p>
              </CardBody>
            </Card>
            <AppCta
              deviceType={deviceType}
              label={deviceType === "Web" ? "Start Earning" : "Get App"}
            />
            <p className="small text-center text-light mt-2 mb-0">
              You set the price. You keep {PRO_PAYOUT_PERCENT}%.{" "}
              <a href="/auth/pricing" className="text-light">
                <u>See how pricing works</u>
              </a>
            </p>
            <PaymentSafetyLine tone="text-light" />
            <UsageProofStrip />

            <Card className="border-0 text-center mt-4 mb-5">
              <CardBody className="py-3">
                <h2 className="h3 text-info mb-1">Not a pro?</h2>
                <p className="text-muted mb-3">
                  Follow local anglers and catch more fish. See what is working
                  and when -- without the guesswork.
                </p>
                <AppCta
                  deviceType={deviceType}
                  color="info"
                  label={
                    deviceType === "Web" ? "Follow local pros" : "Get App"
                  }
                />
                <PaymentSafetyLine />
              </CardBody>
            </Card>
          </Col>
          <Col lg="4">
            <Carousel
              activeIndex={activeIndex}
              next={next}
              previous={previous}
              ride="carousel"
              dark={true}
            >
              <CarouselIndicators
                items={items}
                activeIndex={activeIndex}
                onClickHandler={goToIndex}
              />
              {slides}
              <CarouselControl
                direction="prev"
                directionText="Previous"
                onClickHandler={previous}
              />
              <CarouselControl
                direction="next"
                directionText="Next"
                onClickHandler={next}
              />
            </Carousel>
            <p className="small text-center text-muted mt-3">
              Pros log catches and set up paid plans. Anglers follow a pro,
              pick a plan, and see that pro's catches: lure, location, weather
              and time.
            </p>
          </Col>
        </Row>

        <Testimonials
          wrap={(body) => (
            <Row className="justify-content-center m-4">
              <Col lg="10">
                <Card className="border-0 text-center mb-4">
                  <CardHeader className="bg-transparent">
                    <h2 className="text-uppercase ls-1 text-info pt-3 mb-0">
                      From anglers on Bite Logr
                    </h2>
                  </CardHeader>
                  <CardBody>{body}</CardBody>
                </Card>
              </Col>
            </Row>
          )}
        />

        <Row className="justify-content-center m-4">
          <Col lg="10">
            <Card className="card-pricing border-0 text-center mb-4">
              <CardHeader className="bg-transparent">
                <h2 className="text-uppercase ls-1 text-info pt-3 mb-0">
                  Why?
                </h2>
              </CardHeader>
              <CardBody className="px-lg-5">
                <span className="text-muted">
                  Most pros spend their lives building knowledge they can't
                  monetize. Bite Logr turns your hard-earned expertise into
                  passive income -- and helps teach the next generation how to
                  catch.
                </span>
              </CardBody>
            </Card>

            <Card className="border-0 text-center mb-4">
              <CardHeader className="bg-transparent">
                <h2 className="text-uppercase ls-1 text-info pt-3 mb-0">
                  How it works / What it costs
                </h2>
              </CardHeader>
              <CardBody className="px-lg-5">
                <p className="text-lead mt-0">
                  You set your subscription price. Your followers pay you. We
                  keep {PLATFORM_FEE_PERCENT}% - you keep {PRO_PAYOUT_PERCENT}%.
                </p>
                <p className="text-muted">
                  Payouts go out monthly by direct deposit. Payments are
                  processed by Stripe, and followers can cancel anytime from
                  their account.
                </p>
                <p className="text-muted">
                  Founding Anglers pay zero platform fees for life.{" "}
                  <a href="/auth/foundingangler">See if you qualify</a>.
                </p>
                <AppCta
                  deviceType={deviceType}
                  className="mt-4"
                  label={deviceType === "Web" ? "Start Earning" : "Get App"}
                />
                <PaymentSafetyLine />
              </CardBody>
            </Card>

            <Card className="border-0 mb-4">
              <CardHeader className="bg-transparent text-center">
                <h2 className="text-uppercase ls-1 text-info pt-3 mb-0">
                  Questions anglers ask
                </h2>
              </CardHeader>
              <CardBody className="px-lg-5">
                <FaqList />
                <p className="text-center mb-0">
                  <a href="/auth/faq">More answers in the FAQ</a> ·{" "}
                  <a href="/auth/about">About Bite Logr</a>
                </p>
              </CardBody>
              <CardFooter className="text-center">
                <a
                  className="text-light"
                  href={FACEBOOK_URL}
                  target="_blank"
                  rel="noreferrer"
                >
                  Contact Us
                </a>
              </CardFooter>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
}
